import { customColors } from '@apollo/orbit';
import Downshift from 'downshift';
import { GraphQLSchema } from 'graphql';
import React, { useEffect, useRef } from 'react';

import { useAnalyticsContext } from 'src/app/graph/explorerPage/hooks/useAnalyticsContext';
import { DocsNavigationAction } from 'src/app/graph/explorerPage/hooks/useExplorerState/useDocsPath';
import {
  SearchState,
  SearchStateAction,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/useSearchState';
import {
  hotkeys,
  useKeypressListener,
} from 'src/hooks/useKeypressListener/useKeypressListener';
import { GetSubgraphs } from 'src/lib/graphql/subgraphMetadataHelpers';

import { DocsPathSelection } from '../docsPathSelection/DocsPathSelection';
import { SpotlightSearch } from '../spotlightSearch/SpotlightSearch';

const { bg } = customColors;

export const TriggerableSearch = ({
  dispatchDocsNavAction,
  schema,
  fragmentTypeNames,
  searchState,
  dispatchSearchAction,
  getSubgraphs,
}: {
  dispatchDocsNavAction: React.Dispatch<DocsNavigationAction>;
  schema: GraphQLSchema;
  fragmentTypeNames: string[];
  searchState: SearchState;
  dispatchSearchAction: React.Dispatch<SearchStateAction>;
  getSubgraphs: GetSubgraphs | undefined;
}) => {
  const { track } = useAnalyticsContext();
  const backdropRef = useRef(null);

  useKeypressListener(hotkeys.toggleSearch, (e) => {
    dispatchSearchAction({ type: 'toggle' });
    e?.preventDefault();
    e?.stopPropagation();
  });

  const searchSessionKeystrokesRef = useRef(0);
  const searchSessionStartTimeRef = useRef(0);

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        searchState.state !== 'cancelled' &&
        searchState.state !== 'complete'
      ) {
        dispatchSearchAction({ type: 'cancel' });
      }
      searchSessionKeystrokesRef.current++;
    };
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [dispatchSearchAction, searchState]);

  const searchStateName = searchState.state;
  useEffect(() => {
    if (searchStateName === 'searching') {
      track('Search Session Start');
      searchSessionStartTimeRef.current = Date.now();
      searchSessionKeystrokesRef.current = 0;
    } else if (searchStateName === 'choosing path') {
      track('Search Session Choose Path');
    } else if (
      searchStateName === 'cancelled' ||
      searchStateName === 'complete'
    ) {
      if (searchStateName === 'cancelled') {
        track('Search Session Cancel');
      } else {
        track('Search Session Complete');
      }
      track('Search Session Keyboard NavCount', {
        value: searchSessionKeystrokesRef.current,
      });
      track('Search Session End Duration', {
        value: Date.now() - searchSessionStartTimeRef.current,
      });
    }
  }, [searchStateName, track]);

  // listen for message coming from explorer page iframe to toggle search
  useEffect(() => {
    const onMessage = ({ data }: { data: string }) =>
      data === 'open search' && dispatchSearchAction({ type: 'toggle' });
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [dispatchSearchAction]);

  return (
    <>
      {searchState.visible && (
        <div
          ref={backdropRef}
          className="fixed z-40 size-full"
          style={{
            background: `${bg.black}55`,
          }}
          onClick={(e) => {
            if (e.target === backdropRef.current) {
              // clicked on the backdrop and not its children
              dispatchSearchAction({ type: 'cancel' });
            }
          }}
        >
          <div
            className="absolute inset-x-0 mx-auto w-9/12 max-w-3xl"
            style={{ top: '10vh' }}
          >
            {searchState.state === 'searching' && (
              <SpotlightSearch
                graphqlSchema={schema}
                getSubgraphs={getSubgraphs}
                onSelect={(selectedItem) => {
                  if (!selectedItem) {
                    dispatchSearchAction({
                      type: 'cancel',
                    });
                    return;
                  }
                  dispatchSearchAction({
                    type: 'select field',
                    selectedItem,
                  });
                }}
                onInputValueChange={(searchQuery, stateChangeOptions) => {
                  // This callback is somewhat surprisingly (but understandably)
                  // triggered when a user hits enter to select an option from
                  // the list -- the input value changes from e.g. `use` to `User.id`
                  if (
                    'type' in stateChangeOptions &&
                    stateChangeOptions.type ===
                      Downshift.stateChangeTypes.changeInput
                  ) {
                    // this change was triggered by intentional user input
                    // not by autocomplete (`Downshift.stateChangeTypes.keyDownEnter`)
                    dispatchSearchAction({
                      type: 'set search query',
                      searchQuery,
                    });
                  }
                }}
                onHighlightedIndexChange={(highlightedIndex) =>
                  dispatchSearchAction({
                    type: 'set highlightedIndex',
                    highlightedIndex,
                  })
                }
                initialHighlightedIndex={searchState.highlightedIndex}
                inputValue={searchState.searchQuery ?? ''}
              />
            )}
            {searchState.state === 'choosing path' && (
              <DocsPathSelection
                graphqlSchema={schema}
                fragmentTypeNames={fragmentTypeNames}
                parentType={searchState.fieldInfo.parentMatch.type}
                field={searchState.fieldInfo.fieldMatch.field}
                argument={searchState.fieldInfo.argumentMatch?.argument}
                onSelect={(path) => {
                  if (!path) {
                    dispatchSearchAction({
                      type: 'cancel',
                    });
                    return;
                  }
                  dispatchSearchAction({
                    type: 'select path',
                  });
                  dispatchDocsNavAction({
                    type: 'navigate to',
                    path,
                  });
                }}
                goBackToSearch={(searchQuery?: string) => {
                  dispatchSearchAction({
                    type: 'return to search',
                    searchQuery:
                      searchQuery || searchState.previousState.searchQuery,
                    highlightedIndex:
                      searchState.previousState.highlightedIndex,
                  });
                }}
                onHighlightedIndexChange={(highlightedIndex) =>
                  dispatchSearchAction({
                    type: 'set highlightedIndex',
                    highlightedIndex,
                  })
                }
                initialHighlightedIndex={searchState.highlightedIndex}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
